import { Command, CommandGroupSource, CommandQuery, CommandFunction } from "@omegagrid/commands";
import { msg } from "@omegagrid/localize";
import rp, { RequestParams, CommandProvider, formatDateTime, formatDate } from "@reporting/rp-app-host";
import { HistoryReportContext, ReportContext } from "@reporting/rp-types/analytics";
import { css, html } from "lit";
import { getContextIcon } from "./adapters";
import { AnalyticsApplication } from ".";
import { ModuleCode } from "@reporting/rp-types/accounting";

export type SearchResultCommand = Command & {
	props: {
		context?: ReportContext;
	};
}

const UNDEFINED_USER = 'undefined';

const commandStyle = css`
	.item .command {
		display: flex;
		flex-direction: row;
		height: 40px;
		margin-left: 0px !important;
		padding-left: 0px !important;
	}

	.item .command-icon {
		flex: 0 0 40px;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.item .command-content {
		flex: 1;
	}

	.item .command .command-text {
		flex: 0;
	}

	.item .command .command-details {
		display: flex;
		flex-direction: row;
		font-size: 0.65em;
		gap: 5px;
		white-space: nowrap;
		align-items: center;
		height: 20px;
		padding-right: 15px;
	}

	.item .command-id {
		flex: 1;
	}

	.item .command-name, .item .command-time {
		flex: 0;
	}
`;

const commandRenderer = (_div: HTMLDivElement, command: SearchResultCommand) => {
	const context = command.props?.context || {};
	const module = AnalyticsApplication.get<AnalyticsApplication>()?.modules?.get(context.module as ModuleCode);
	return html`
		<div class="command">
			<div class="command-icon">
				<og-icon .icon="${getContextIcon(context, module)}" size="lg"></og-icon>
			</div>

			<div class="command-content">
				<div class="command-details">
					<div class="command-id">#${context.id_context}</div>
					<div class="command-user">@${context.user_name ? context.user_name : ''}</div>
					<div class="command-time">${context.date_created ? formatDateTime(context.date_created) : ''}</div>
				</div>

				<div class="command-text">
					${command.name}
				</div>
			</div>
		</div>
	`;
}

const exec: CommandFunction = (c: Command) => rp.host.open(c.id, {title: c.name});

export class ReportSearchProvider implements CommandProvider {

	id = 'report';
	title = msg('Views');
	placeholder = msg('Search views...');
	triggerCommand = 'v';

	switches = [
		{ key: 'history', value: msg('History'), checked: false },
		(rp.userProvider.hasRight('admin') ? { key: 'all', value: msg('All users'), checked: false } : null),
	].filter(Boolean);


	async getHistoryGroups(filter: string, allUsers: boolean) {
		const params: RequestParams = {
			fields: ['id_context', 'accounting', 'name', 'module', 'user_name', 'context_view', 'date_created', 'date'],
			all_users: allUsers ? 1 : 0
		};

		if (filter) {
			if (filter.startsWith('@') && allUsers) {
				params.where = `user_name='~${filter.substring(1)}~' or login_name='~${filter.substring(1)}~'`;
			} else {
				params.where = `name='~${filter}~'`;
			}
		}

		if (allUsers) params.fields.push('login_name');
		
		const contexts = await rp.ms('analytics').get<HistoryReportContext[]>('/contextHistory', params);
		const groups: CommandGroupSource[] = [];
		let group: CommandGroupSource;
		let lastGroup: string;
			
		if (allUsers) {
			for (let i = 0; i < contexts.length; i++) {
				const context = contexts[i];
				if (context.user_name == null) {
					lastGroup = null;
					group = {id: `user-0`, name: `@${UNDEFINED_USER}`, commands: []};
					groups.push(group);
				} else if (context.user_name != lastGroup) {
					lastGroup = context.user_name;
					group = {id: `user-${lastGroup}`, name: `@${context.login_name} (${context.user_name})`, commands: []};
					groups.push(group);
				}

				group.commands.push({
					id: `analytics-reportContainer-${context.id_context}-${i}`,
					name: context.name || `#${context.id_context}`,
					group: group.id,
					props: { context },
					exec: exec
				});
			}
		} else {
			for (let i = 0; i < contexts.length; i++) {
				const context = contexts[i];
				if (context.user_name == null) {
					lastGroup = null;
					group = {id: `user-0`, name: `@${UNDEFINED_USER}`, commands: []};
					groups.push(group);
				} else if (context.date != lastGroup) {
					lastGroup = context.date;
					group = {id: `date-${lastGroup}`, name: formatDate(context.date), commands: []};
					groups.push(group);
				}

				group.commands.push({
					id: `analytics-reportContainer-${context.id_context}-${i}`,
					name: context.name || `#${context.id_context}`,
					group: group.id,
					props: { context },
					exec: exec
				});
			}
		}

		return groups;
	}

	async getContextGroups(filter: string, allUsers: boolean) {
		const params: RequestParams = {
			fields: ['id_context', 'accounting', 'name', 'module', 'user_name', 'context_view', 'date_created'],
			order: allUsers ? ['user_name', 'name'] : ['name'],
			all_users: allUsers ? 1 : 0
		};

		if (filter) {
			if (filter.startsWith('@') && allUsers) {
				params.where = `user_name='~${filter.substring(1)}~' or login_name='~${filter.substring(1)}~'`;
			} else {
				params.where = `name='~${filter}~'`;
			}
		}

		if (allUsers) params.fields.push('login_name');

		const contexts = await rp.ms('analytics').get<ReportContext[]>('/namedContexts', params);
		const groups: CommandGroupSource[] = [];
		let group: CommandGroupSource;

		if (allUsers) {
			let lastGroup: string;
			for (let i = 0; i < contexts.length; i++) {
				const context = contexts[i];
				if (context.user_name == null) {
					lastGroup = null;
					group = {id: `user-0`, name: `@${UNDEFINED_USER}`, commands: []};
					groups.push(group);
				} else if (context.user_name != lastGroup) {
					lastGroup = context.user_name;
					group = {id: `user-${lastGroup}`, name: `@${context.login_name} (${context.user_name})`, commands: []};
					groups.push(group);
				}

				group.commands.push({
					id: `analytics-reportContainer-${context.id_context}`,
					name: context.name,
					group: group.id,
					props: { context },
					exec: exec
				});
			}
		} else {
			group = {id: 'default', name: msg('Views'), commands: []};
			groups.push(group);
			for (let i = 0; i < contexts.length; i++) {
				const context = contexts[i];
				group.commands.push({
					id: `analytics-reportContainer-${context.id_context}`,
					name: context.name,
					group: group.id,
					props: { context },
					exec: exec
				});
			}
		}

		return groups;
	}

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		const filter = query?.filter ?? '';
		const history = query?.switches?.some(s => s === 'history') ?? false;
		const allUsers = query?.switches?.some(s => s === 'all') ?? false;
		return history ? this.getHistoryGroups(filter, allUsers) : this.getContextGroups(filter, allUsers);
	}

	getOptions = () => ({
		commandRenderer: commandRenderer,
		itemHeight: 40,
		itemStyle: commandStyle
	});

}
